import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { notifyGladlyNavigate, shouldForceFullPageLoad } from '@/utils/gladly';

/**
 * Keeps Gladly in step with SPA route changes.
 *
 * Gladly requires `Gladly.navigate()` to be called on every SPA route change,
 * and the Help Center boots once per page load, so it cannot share the SPA
 * router. Moving into or out of `/faq` therefore forces a full page load
 * (the URL has already changed, so the reload fetches the new route as a fresh
 * document); every other change just notifies Gladly.
 *
 * See docs/research/gladly-chat-and-help-center.md, section "SPA behaviour".
 */
const useGladlyRouteSync = () => {
  const { pathname, search } = useLocation();
  const prevPathname = useRef(undefined);

  useEffect(() => {
    const previous = prevPathname.current;
    prevPathname.current = pathname;

    // Initial page load: Gladly boots on its own.
    if (previous === undefined) {
      return;
    }

    if (shouldForceFullPageLoad(previous, pathname)) {
      window.location.reload();
      return;
    }

    notifyGladlyNavigate(window);
  }, [pathname, search]);
};

export default useGladlyRouteSync;
